import React, { createContext, useContext } from 'react'

import { CustomerQuery } from 'src/apollo/onlineOrdering'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import useTracker from 'src/lib/js/hooks/useTracker'

export type PasswordlessLoginResult = {
  success: boolean
  errorCode?: string
  errorMessage?: string
}

export interface CustomerContextCommonType {
  customer?: Customer | null
  loadingCustomer: boolean
  refetchCustomer: () => void
  refreshAuthToken: () => void
  fetchCustomer: () => Promise<Customer | null>
  passwordlessLogin: (phoneNumber: string) => Promise<PasswordlessLoginResult>
  passwordlessConfirmCode: (
    phoneNumber: string,
    code: string
  ) => Promise<string | null>
  completeSignup: (
    email: string,
    firstName: string,
    lastName: string,
    delay?: number
  ) => Promise<boolean>
  pwlessLogout: () => Promise<boolean>
  customerContextMode: 'PASSWORDLESS_AUTH'
}

export interface CustomerContextType extends CustomerContextCommonType {
  /**
   * Whether a guest is signed in. Derived from `customer` by the provider, so it
   * is false while a fetch is still in flight — read `loadingCustomer` alongside
   * it to tell "signed out" apart from "not known yet".
   */
  isLoggedIn: boolean
  savedAddressUsed: boolean
  setSavedAddressUsed: (savedAddressUsed: boolean) => void
}

export type Customer =
  | (CustomerQuery['customer'] & { __typename: 'Customer' })
  | undefined
  | null

export const CustomerContext = createContext<CustomerContextType | undefined>(
  undefined
)

function isPromise<T>(v: unknown): v is Promise<T> {
  return (
    !!v &&
    typeof v === 'object' &&
    'then' in v &&
    typeof v.then === 'function' &&
    'catch' in v &&
    typeof v.catch === 'function'
  )
}

function useWrapCustomerActionsWithTracker<
  TFn extends (...args: any[]) => ReturnType<TFn>
>(
  fn: TFn,
  trackingParams: {
    eventSuccessName: TrackingEvents
    eventErrorName: TrackingEvents
    customerContextMode: CustomerContextCommonType['customerContextMode']
  }
): (...args: Parameters<TFn>) => ReturnType<TFn> {
  const { track } = useTracker()
  return React.useCallback(
    (...args) => {
      try {
        const res = fn(...args)
        // handle promises
        if (isPromise(res)) {
          return res
            .then((r) => {
              track(trackingParams.eventSuccessName, {
                customerContextMode: trackingParams.customerContextMode
              })
              return r
            })
            .catch((e) => {
              track(trackingParams.eventErrorName, {
                customerContextMode: trackingParams.customerContextMode
              })
              throw e
            }) as ReturnType<TFn> // this is a typescript kludge that guarantees we have not modified the underlying value
        } else {
          track(trackingParams.eventSuccessName, {
            customerContextMode: trackingParams.customerContextMode
          })
          return res
        }
      } catch (e) {
        track(trackingParams.eventErrorName, {
          customerContextMode: trackingParams.customerContextMode
        })
        throw e
      }
    },
    [
      fn,
      track,
      trackingParams.eventSuccessName,
      trackingParams.eventErrorName,
      trackingParams.customerContextMode
    ]
  )
}

export const CustomerContextCommonProvider = (
  props: React.PropsWithChildren<{ context: CustomerContextCommonType }>
) => {
  const [savedAddressUsed, setSavedAddressUsed] = React.useState(false)

  const customerContextMode = props.context.customerContextMode
  const passwordlessLogin = useWrapCustomerActionsWithTracker(
    props.context.passwordlessLogin,
    {
      eventSuccessName: TrackingEvents.PASSWORDLESS_LOGIN_SUCCESS,
      eventErrorName: TrackingEvents.PASSWORDLESS_LOGIN_ERROR,
      customerContextMode
    }
  )

  const pwlessLogout = useWrapCustomerActionsWithTracker(
    props.context.pwlessLogout,
    {
      eventSuccessName: TrackingEvents.PWLESS_LOGOUT_SUCCESS,
      eventErrorName: TrackingEvents.PWLESS_LOGOUT_ERROR,
      customerContextMode
    }
  )

  const completeSignup = useWrapCustomerActionsWithTracker(
    props.context.completeSignup,
    {
      eventSuccessName: TrackingEvents.COMPLETE_SIGNUP_SUCCESS,
      eventErrorName: TrackingEvents.COMPLETE_SIGNUP_ERROR,
      customerContextMode
    }
  )

  const refetchCustomer = useWrapCustomerActionsWithTracker(
    props.context.refetchCustomer,
    {
      eventSuccessName: TrackingEvents.REFETCH_CUSTOMER_SUCCESS,
      eventErrorName: TrackingEvents.REFETCH_CUSTOMER_ERROR,
      customerContextMode
    }
  )

  const fetchCustomer = useWrapCustomerActionsWithTracker(
    props.context.fetchCustomer,
    {
      eventSuccessName: TrackingEvents.FETCH_CUSTOMER_SUCCESS,
      eventErrorName: TrackingEvents.FETCH_CUSTOMER_ERROR,
      customerContextMode
    }
  )

  const refreshAuthToken = useWrapCustomerActionsWithTracker(
    props.context.refreshAuthToken,
    {
      eventSuccessName: TrackingEvents.REFRESH_AUTH_TOKEN_SUCCESS,
      eventErrorName: TrackingEvents.REFRESH_AUTH_TOKEN_ERROR,
      customerContextMode
    }
  )

  const context = {
    ...props.context,
    passwordlessLogin,
    pwlessLogout,
    completeSignup,
    refetchCustomer,
    fetchCustomer,
    refreshAuthToken,
    isLoggedIn: Boolean(props.context.customer),
    savedAddressUsed,
    setSavedAddressUsed
  }

  return (
    <CustomerContext.Provider value={context}>
      {props.children}
    </CustomerContext.Provider>
  )
}

export const useCustomer = () => {
  const context = useContext(CustomerContext)
  if (!context) {
    throw new Error('useCustomer must be used within a CustomerContextProvider')
  }

  return context
}
